import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { PackageCheck, Lock, ArrowLeft, AlertTriangle, Check } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { cn } from '../../utils/cn';
import type { NurseryPicker } from '../../types/nursery';

/**
 * /nursery/pickers — which farms each Order Picker works.
 *
 * A picker sees only the loads despatched from their farms, and a picker with
 * no farm sees nothing — so an empty row here is the one thing on the page
 * worth shouting about. Plan: docs/nursery-despatch-plan.md.
 */

const sameSet = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort((x, y) => x - y).every((v, i) => v === [...b].sort((x, y) => x - y)[i]);

function NurseryPickers() {
  const navigate = useNavigate();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [pickers, setPickers] = useState<NurseryPicker[]>([]);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [draft, setDraft] = useState<Record<number, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getPickers();
      if (res.success) {
        setPickers(res.data.pickers);
        setFarms(res.data.farms);
        setDraft(Object.fromEntries(res.data.pickers.map((p) => [p.user_id, p.farm_ids])));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pickers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const toggle = (userId: number, farmId: number) => {
    setSavedId(null);
    setDraft((d) => {
      const cur = d[userId] ?? [];
      return { ...d, [userId]: cur.includes(farmId) ? cur.filter((f) => f !== farmId) : [...cur, farmId] };
    });
  };

  const save = async (picker: NurseryPicker) => {
    setSavingId(picker.user_id);
    setError(null);
    try {
      const res = await nurseryService.setPickerFarms(picker.user_id, draft[picker.user_id] ?? []);
      if (res.success) {
        setPickers((ps) => ps.map((p) => (p.user_id === picker.user_id ? { ...p, farm_ids: res.data.farm_ids } : p)));
        setDraft((d) => ({ ...d, [picker.user_id]: res.data.farm_ids }));
        setSavedId(picker.user_id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  if (licenseLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Manager license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Assigning pickers to farms requires a <span className="font-medium">Nursery Manager</span> or{' '}
            <span className="font-medium">Nursery Admin</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to nursery
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Order Pickers"
        subtitle="Choose the farms each picker works. A picker sees only the loads despatched from their farms."
        icon={<PackageCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : pickers.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
            <PackageCheck className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
            <h2 className="text-base font-semibold text-neutral-800 mb-1">Nobody holds an Order Picker seat yet</h2>
            <p className="text-sm text-neutral-500">
              The seat is free. Give it to someone from the Nursery module's Seats &amp; Users page on{' '}
              <Link to="/subscriptions" className="text-primary-600 hover:underline">Subscriptions</Link>,
              then come back here to choose their farms.
            </p>
          </div>
        ) : farms.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
            This organisation has no farms yet, so there is nowhere to assign a picker. Add a farm under{' '}
            <Link to="/orchard" className="font-medium underline">Farms</Link> first.
          </div>
        ) : (
          pickers.map((p) => {
            const chosen = draft[p.user_id] ?? [];
            const dirty = !sameSet(chosen, p.farm_ids);
            return (
              <div key={p.user_id} className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-neutral-900 break-words">{p.name || p.email}</div>
                    {p.name && p.email && <div className="text-xs text-neutral-500 break-all">{p.email}</div>}
                  </div>
                  {p.farm_ids.length === 0 && !dirty && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                      No farms. This picker sees nothing.
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2" role="group" aria-label={`Farms for ${p.name || p.email}`}>
                  {farms.map((f) => {
                    const on = chosen.includes(f.id);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggle(p.user_id, f.id)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm max-w-full break-words text-left',
                          on
                            ? 'bg-primary-50 border-primary-300 text-primary-800'
                            : 'bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50',
                        )}
                      >
                        {on && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
                        {f.name}
                      </button>
                    );
                  })}
                </div>

                {(dirty || savedId === p.user_id) && (
                  <div className="flex items-center justify-end gap-3 mt-3">
                    {savedId === p.user_id && !dirty && <span className="text-xs text-green-700">Saved</span>}
                    {dirty && (
                      <Button size="sm" onClick={() => save(p)} disabled={savingId === p.user_id}>
                        {savingId === p.user_id ? 'Saving…' : 'Save farms'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

export default NurseryPickers;
